import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Dialog, Grid, IconButton, Typography, useMediaQuery, useTheme } from "@mui/material";
import { Download } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import { History } from "lucide-react";
import CustomTable from "../../common/CustomTable";
import { Column, setSingleAlertObj, SingleAlertInfo, useCommonReducer } from "../../../redux/slice/commonSlice";
import { activateProductDocument, fetchProductDocumentsByProductId, getInactiveProductDocuments, getProductPricingHistory, updateElementInProductSlice, UpdateProductDocumentStatusRequest, useProductReducer, ProductDocumentListInfo } from "../../../redux/slice/product/createProductSlice";
import { useAppDispatch } from "../../../redux/slice/redux-hooks";
import CustomCurrencyField from "../../common/CustomCurrencyField";
import CustomCommentPreview from "../../common/CustomCommentPreview";
import CustomIconButton from "../../common/CustomIconButton";
import { tokens } from "../../../theme";
import { PRODUCT_DOCUMENT_STATUS, PRODUCT_DOCUMENT_TYPES, PRODUCT_DOCUMENT_TYPES_TITLE_CASE } from "../../../constants/applicationConstants";
import { getCodeTableValuesByTableName, useCodetablesSlice } from "../../../redux/slice/codetableSlice";
import { useModalStyles } from "../../common/useModalStyles";

interface ProductDocumentHistoryProps {
    open: boolean;
    onClose: () => void;
}

const ProductDocumentHistory: React.FC<ProductDocumentHistoryProps> = ({
    open,
    onClose
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const isDark = theme.palette.mode === "dark";
    const {
        tokens: modalTokens,
        paperSx,
        headerSx,
        iconBoxSx,
        closeIconSx,
        bodySx,
        footerSx,
        cancelBtnSx,
    } = useModalStyles();
    const colors = tokens(theme.palette.mode);
    const dispatch = useAppDispatch();
    const { productObject, inactivatedProductDocuments, activateProductDocumentStatus } = useProductReducer();
    const [productId, setProductId] = useState<number | null>(null);
    const [productDocumentTypeMap, setProductDocumentTypeMap] = useState<Record<string, string>>({});
    const { allCodeTablesValuesMap, allCodeTableValueFetchStatus } = useCodetablesSlice();

    const columnsDocuments: Column[] = useMemo(() => {
        return [
            { id: "documentType", label: "Type", minWidth: 100 },
            { id: "docName", label: "Name", minWidth: 100 },
            { id: "manufacturer", label: "Manufacturer", minWidth: 100 },
            { id: "status", label: "Status", minWidth: 100 },
            { id: "inactiveDate", label: "Inactive Date", minWidth: 100 },
            { id: "comments", label: "Comment", minWidth: 100 },
            { id: "documentURL", label: "Document URL", minWidth: 100, },
            { id: "action", label: "Action", minWidth: 80 },
        ]
    }, [open]);

    useEffect(() => {
        const urlSearchParams = new URLSearchParams(window.location.search);
        let id = urlSearchParams.get('id');
        if(id) {
            setProductId(parseInt(id));
        };
        if (open) {
            dispatch(getInactiveProductDocuments({ productId: productObject.productId }))
        }
    }, [open]);

    useEffect(() => {
        if (allCodeTableValueFetchStatus === "success" && allCodeTablesValuesMap) {
          const sortedDocumentList = [
            ...getCodeTableValuesByTableName(allCodeTablesValuesMap, "ProductDocument"),
          ].sort((a, b) => a.displayValue.localeCompare(b.displayValue));
          const productDocumentTypeMap = Object.fromEntries(
            sortedDocumentList.map(item => [item.keyName, item.displayValue])
          );
          setProductDocumentTypeMap(productDocumentTypeMap)
        }
      }, [allCodeTablesValuesMap, allCodeTableValueFetchStatus]);

    const commentComponent = useCallback((row: any) => {
        return (<>
            <Grid item>
                {
                    row.comments ?
                        <CustomCommentPreview
                            title={`Comment`}
                            commentBody={row.comments}
                        /> : "N/A"}
            </Grid>
        </>)
    }, [inactivatedProductDocuments]);

    const URLComponent = useCallback((row: any) => {
        const url = typeof row.documentURL === "string" ? row.documentURL : null;
      
        if (!url) {
          return null;
        }
      
        return (
          <>
            <Grid>
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                style={{ textDecoration: "underline", color: colors.info[300] }}
              >
                {url.length > 30 ? `${url.substring(0, 30)}...` : url}
              </a>
            </Grid>
          </>
        );
      }, [colors.info]);

    const productDocumentRows: ProductDocumentListInfo[] = useMemo(() => {
        const rows: ProductDocumentListInfo[] = []
        inactivatedProductDocuments?.forEach(pd => {
            if (pd.entityName === 'Document' && pd.docName !== null) {
                const row = {
                    ...pd,
                    documentType: productDocumentTypeMap[pd.documentType],
                    documentURL: URLComponent(pd),
                    comments: commentComponent(pd),
                    manufacturer: pd.manufacturer?.manufacturerName || "",
                }
                rows.push(row);
            }
        })
        return rows;
    }, [inactivatedProductDocuments]);

    const statusUpdateDocumentTypes = [
        PRODUCT_DOCUMENT_TYPES_TITLE_CASE.IVR_FORM,
        PRODUCT_DOCUMENT_TYPES_TITLE_CASE.ORDER_FORM, 
        PRODUCT_DOCUMENT_TYPES_TITLE_CASE.SERVICE_AGREEMENT, 
        PRODUCT_DOCUMENT_TYPES_TITLE_CASE.TISSUE_TRACKING_FORM, 
        PRODUCT_DOCUMENT_TYPES_TITLE_CASE.RETURN_FORM
        ];

    const handleProductDocumentActivate = useCallback((row: ProductDocumentListInfo) => {
        let activateRequest: UpdateProductDocumentStatusRequest = {
            productId: row.productId,
            documentId: parseInt(row.entityId) 
        }
        if(statusUpdateDocumentTypes.includes(row.documentType)) {
            activateRequest.status = PRODUCT_DOCUMENT_STATUS.PENDING
        } 
        dispatch(activateProductDocument({activateRequest: activateRequest}));
    }, [open]);

    useEffect(() => {
        if(activateProductDocumentStatus === 'success') {
            let successObj: SingleAlertInfo = {
                message: 'Product Document successfully activated.',
                alertType: "success",
              }
              dispatch(setSingleAlertObj(successObj));
              dispatch(updateElementInProductSlice({
                elementName: "activateProductDocumentStatus",
                value: "idle"
              }));
              productId && dispatch(fetchProductDocumentsByProductId(productId));
              onClose();
        }
     }, [activateProductDocumentStatus]);

    const productDocActionComponent = useCallback((row: ProductDocumentListInfo, rowIndex: number): JSX.Element => {
        return (
          <>
            <CustomIconButton color="info" size="small" variant="outlined" onClick={() => handleProductDocumentActivate(row)}
              tooltipMessage="Activate" tooltipPlacement='bottom'>
              <Download />
            </CustomIconButton>
          </>
        );
      }, [open]);

    return (
        <>
            <Dialog
                open={open}
                keepMounted
                onClose={onClose}
                maxWidth="lg"
                fullWidth
                fullScreen={isMobile}
                scroll="paper"
                PaperProps={{ sx: paperSx(isMobile) }}
                onClick={(event) => {
                    event.stopPropagation();
                }}
                aria-describedby="product-document-history-dialog-description"
            >
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        flex: 1,
                        minHeight: 0,
                        overflow: "hidden",
                    }}
                >
                    <Box sx={headerSx}>
                        <Box sx={iconBoxSx(modalTokens.accent)}>
                            <History size={20} color={modalTokens.accent} strokeWidth={1.8} />
                        </Box>
                        <Box sx={{ flex: 1, pr: 4 }}>
                            <Typography fontWeight={700} sx={{ fontSize: "1.1rem", color: modalTokens.textPrimary, lineHeight: 1.3 }}>
                                Product Document History
                            </Typography>
                            <Typography id="product-document-history-dialog-description" sx={{ fontSize: "0.8rem", color: modalTokens.textSecondary, mt: 0.15 }}>
                                Review inactive product documents and activate them when required.
                            </Typography>
                        </Box>
                        <IconButton onClick={onClose} size="small" sx={closeIconSx} aria-label="Close">
                            <CloseIcon sx={{ fontSize: "1rem" }} />
                        </IconButton>
                    </Box>

                    <Box
                        sx={{
                            ...bodySx,
                            minHeight: 0,
                            overflowY: "auto",
                            overflowX: "hidden",
                            scrollbarGutter: "stable",
                            "&::-webkit-scrollbar": { width: 6, height: 6 },
                            "&::-webkit-scrollbar-track": { background: "transparent" },
                            "&::-webkit-scrollbar-thumb": {
                                background: isDark ? "rgba(255,255,255,0.12)" : "#CBD5E1",
                                borderRadius: 4,
                            },
                        }}
                    >
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <CustomTable
                                    columns={columnsDocuments}
                                    rows={productDocumentRows}
                                    disablePagination={true}
                                    borderBox={true}
                                    actionComponent={(row: any, rowIndex: number) => productDocActionComponent(row, rowIndex)}
                                    showMenu={true}
                                    sx={{
                                        width: "100%",
                                        maxWidth: "100%",
                                    }}
                                />
                            </Grid>
                        </Grid>
                    </Box>

                    <Box sx={footerSx}>
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "flex-end",
                                gap: 1.5,
                                flexDirection: { xs: "column", sm: "row" },
                            }}
                        >
                            <Box
                                component="button"
                                type="button"
                                onClick={onClose}
                                sx={{
                                    ...cancelBtnSx,
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: 0.8,
                                    px: 3,
                                    py: 1.2,
                                    border: "1px solid",
                                    cursor: "pointer",
                                    order: { xs: 2, sm: 1 },
                                }}
                            >
                                Cancel
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </Dialog>
        </>
    );
};

export default ProductDocumentHistory;
